import React, { useState, useRef, useEffect } from 'react';
import type { KeywordSearchResultItem } from '../types';
import { exportSearchMatrix, exportDetailedMatches } from '../utils/exportUtils';
import { exportResultsHtml } from '../utils/compareExport';
import type { ColoredTerm } from '../utils/highlightColors';

interface ExportDropdownProps {
  results: KeywordSearchResultItem[];
  /** Ordered query rows + colors — when present the HTML export keeps each
   *  row's highlight color (matches the results/compare views). */
  coloredTerms?: ColoredTerm[];
  /** The keyword/phrase — used for the export title + filename. */
  keyword?: string;
  /** Near on-demand: rows arrive without passages. When set, every export
   *  action awaits this first (it rejects on failure, which aborts the export)
   *  and exports what it resolves to instead of `results`. */
  loadResults?: () => Promise<KeywordSearchResultItem[]>;
}

const ExportDropdown: React.FC<ExportDropdownProps> = ({ results, coloredTerms, keyword, loadResults }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const disabled = results.length === 0 || loading;

  // Inline mode (no loadResults): synchronous, exactly as before. On-demand:
  // fetch passages first; a failed load aborts the export (never empty excerpts).
  const runExport = async (doExport: (rows: KeywordSearchResultItem[]) => void | Promise<void>) => {
    if (!loadResults) {
      await doExport(results);
      return;
    }
    setLoading(true);
    let rows: KeywordSearchResultItem[];
    try {
      rows = await loadResults();
    } catch {
      // loadResults already routed the failure (refetch); nothing to export.
      return;
    } finally {
      setLoading(false);
    }
    await doExport(rows);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
          disabled
            ? 'border-slate-200 text-slate-400 cursor-not-allowed'
            : 'border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 hover:bg-indigo-50'
        }`}
        title="Export results"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {loading ? 'Preparing…' : 'Export'}
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-30">
          <button
            onClick={() => {
              setIsOpen(false);
              void runExport((rows) => exportResultsHtml(rows, keyword ?? 'keyword', coloredTerms));
            }}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <p className="font-medium">Results (HTML, highlighted)</p>
              <p className="text-xs text-slate-400">Excerpts + row colors, opens offline</p>
            </div>
          </button>
          <button
            onClick={async () => {
              setIsOpen(false);
              // Lazy-loaded: the docx library only ships when this is used.
              const { downloadResultsDocx } = await import('../utils/resultsExportDocx');
              await runExport(async (rows) => {
                await downloadResultsDocx(rows, keyword ?? 'keyword', coloredTerms);
              });
            }}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <p className="font-medium">Results (Word, highlighted)</p>
              <p className="text-xs text-slate-400">Excerpts + row colors, .docx</p>
            </div>
          </button>
          <button
            onClick={() => {
              setIsOpen(false);
              void runExport((rows) => exportSearchMatrix(rows));
            }}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <div>
              <p className="font-medium">Search Matrix (CSV)</p>
              <p className="text-xs text-slate-400">Drug names, links, match counts</p>
            </div>
          </button>
          <button
            onClick={() => {
              setIsOpen(false);
              void runExport((rows) => exportDetailedMatches(rows));
            }}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <p className="font-medium">Detailed Matches (CSV)</p>
              <p className="text-xs text-slate-400">All sections, snippets, per drug</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;
